import { useEffect, useRef, useState } from "react";

import { useParams } from "react-router-dom";
import { MoonLoader } from "react-spinners";

import { formatHtml } from "@/lib/utils";

import { useForm } from "@/hooks/server-state/use-form";
import useBreadcrumbStore from "@/hooks/store/use-breadcrumb";

import Editor from "@/components/widget/editor";
import HtmlEditorArea from "@/components/widget/editor/editorHtmlView";

import FormEditorSidebar from "../_components/editor/sidebar";
import ToolbarTabs from "../_components/editor/toolbar-tabs";

const FormEditorPage = () => {
  const { updateLastBreadcrumb } = useBreadcrumbStore();
  const { formId } = useParams<{ formId: string }>();
  const { data, isLoading } = useForm(formId ?? "");
  const [editorData, setEditorData] = useState<string>("");
  const editorRef = useRef<HTMLDivElement>(null);
  const [isHtmlMode, setIsHtmlMode] = useState<boolean>(false);
  const [htmlDraft, setHtmlDraft] = useState<string>("");
  const initialHtmlRef = useRef<string | null>(null);
  const savedHtmlRef = useRef<string | null>(null);

  const createEditorSnapshot = () => {
    if (!editorRef.current) return "";

    const wrapper = document.createElement("div");
    wrapper.style.cssText = editorRef.current.style.cssText;
    wrapper.innerHTML = editorRef.current.innerHTML;

    return wrapper.outerHTML;
  };

  useEffect(() => {
    if (data?.htmlFormBody) {
      const formParent = document.createElement("div");
      formParent.innerHTML = data.htmlFormBody;

      const firstElementChild = formParent.firstElementChild as HTMLElement;

      if (firstElementChild && editorRef.current) {
        editorRef.current.style.cssText = firstElementChild.style.cssText;
        setEditorData(firstElementChild.innerHTML);

        const outer = firstElementChild.outerHTML;
        const pretty = formatHtml(outer);

        if (!initialHtmlRef.current) {
          initialHtmlRef.current = outer;
        }
        setHtmlDraft(pretty);
      } else {
        const pretty = formatHtml(data.htmlFormBody);
        if (!initialHtmlRef.current) initialHtmlRef.current = data.htmlFormBody;
        setHtmlDraft(pretty);
        setEditorData(data.htmlFormBody);
      }
    }

    updateLastBreadcrumb(data?.name ?? "");
  }, [data]);

  const handleSaveHtml = (editedHtml: string) => {
    const tmp = document.createElement("div");
    tmp.innerHTML = editedHtml;
    const first = tmp.firstElementChild as HTMLElement | null;

    if (first && editorRef.current) {
      editorRef.current.style.cssText = first.style.cssText || "";
      setEditorData(first.innerHTML);
    } else {
      if (editorRef.current) editorRef.current.style.cssText = "";
      setEditorData(editedHtml);
    }

    savedHtmlRef.current = editedHtml;
    setHtmlDraft(formatHtml(editedHtml));
  };

  const handleResetHtml = () => {
    const target = savedHtmlRef.current ?? initialHtmlRef.current;
    if (!target) return;

    const tmp = document.createElement("div");
    tmp.innerHTML = target;
    const first = tmp.firstElementChild as HTMLElement | null;

    if (first && editorRef.current) {
      editorRef.current.style.cssText = first.style.cssText || "";
      setEditorData(first.innerHTML);
    } else {
      setEditorData(target);
    }

    setHtmlDraft(formatHtml(target));
  };

  const handleHtmlModeChange = (active: boolean) => {
    if (active && editorRef.current) {
      const snapshot = createEditorSnapshot();
      if (snapshot) {
        setHtmlDraft(formatHtml(snapshot));
      }
    }

    setIsHtmlMode(active);
  };

  if (isLoading) return <Loading />;
  if (!data) return <EmptyState />;

  return (
    <>
      <ToolbarTabs
        editorRef={editorRef}
        onHtmlModeChange={handleHtmlModeChange}
        htmlDraft={htmlDraft}
        onSaveHtml={handleSaveHtml}
        onResetHtml={handleResetHtml}
        />

      <div className="relative flex">
        <div className="flex h-fit w-full justify-center">
          {isHtmlMode ? (
            <div className="w-full px-6">
              <HtmlEditorArea
                value={htmlDraft}
                onChange={setHtmlDraft}
                readOnly={false}
              />

              <div style={{ display: "none" }}>
                <Editor
                  ref={editorRef}
                  value={editorData}
                  onEditorChange={setEditorData}
                  className="prose-editor"
                />
              </div>
            </div>
          ) : (
            <Editor
              ref={editorRef}
              value={editorData}
              onEditorChange={setEditorData}
              className="prose-editor h-fit shrink-0 -translate-y-[32%] scale-[0.3] shadow-[20px_10px_15px_0px_rgba(0,0,0,0.1)] sm:-translate-y-[20%] sm:scale-50 md:-translate-y-[10%] md:scale-75 lg:translate-y-[5%] lg:scale-100 2xl:translate-y-[15%] 2xl:scale-[1.2]"
            />
          )}
        </div>
        <div className="fixed left-0 top-1/2 z-[9999] -translate-y-1/2">
          <FormEditorSidebar
            editorRef={editorRef}
            setEditorData={setEditorData}
          />
        </div>
      </div>
    </>
  );
};

const Loading = () => (
  <div className="flex h-[calc(100vh/2)] w-full items-center justify-center">
    <MoonLoader color="#0099A5" size={50} />
  </div>
);

const EmptyState = () => (
  <div className="flex h-screen w-full items-center justify-center">
    <p className="text-slate-500">فرم پیدا نشد</p>
  </div>
);

export default FormEditorPage;
